import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";
import App from "./App.jsx";
import Preloader from "./components/Preloader.jsx";
import "./index.css";

// Create a client with NO caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0, // Always fetch fresh data
      cacheTime: 0, // No caching at all
      refetchOnMount: true, // Always refetch on component mount
      refetchOnReconnect: true, // Refetch when reconnecting
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <Preloader />
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 2000,
            style: {
              background: "#ffffffff",
              color: "#000000ff",
            },
            success: {
              duration: 1000,
              iconTheme: {
                primary: "#22c55e",
                secondary: "#fff",
              },
            },
            error: {
              duration: 3000,
              iconTheme: {
                primary: "#ef4444",
                secondary: "#fff",
              },
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);

// Enhanced Service Worker registration with update handling
if ("serviceWorker" in navigator) {
  const refreshServiceWorker = async (registration) => {
    await registration.update();

    if (registration.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  };

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        console.log("✅ Service Worker registered:", registration.scope);
        refreshServiceWorker(registration);

        // Check for updates every 60 seconds and whenever the tab becomes active.
        setInterval(() => {
          refreshServiceWorker(registration);
        }, 60000);

        const handleVisibilityChange = () => {
          if (document.visibilityState === "visible") {
            refreshServiceWorker(registration);
          }
        };

        const handleFocus = () => {
          refreshServiceWorker(registration);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        // Handle service worker updates
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          console.log("🔄 New Service Worker found, installing...");

          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              console.log(
                "✅ New Service Worker installed, will activate on next page load",
              );

              // Optionally notify user about update
              // You can show a toast or banner here
              console.log(
                "💡 App update available. Refresh to get the latest version.",
              );
            }
          });
        });
      })
      .catch((error) => {
        console.log("❌ Service Worker registration failed:", error);
      });
  });

  // Listen for controller change (new SW activated)
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    console.log("🔄 Service Worker controller changed, reloading page...");
    window.location.reload();
  });
}

// Add error boundary for failed resource loads
window.addEventListener(
  "error",
  (event) => {
    // Check if it's a resource loading error
    if (
      event.target !== window &&
      (event.target.tagName === "SCRIPT" || event.target.tagName === "LINK")
    ) {
      console.error(
        "❌ Resource failed to load:",
        event.target.src || event.target.href,
      );

      // If it's a critical script, try to reload the page once
      if (
        event.target.tagName === "SCRIPT" &&
        !sessionStorage.getItem("reload-attempted")
      ) {
        console.log("🔄 Attempting page reload due to failed script load...");
        sessionStorage.setItem("reload-attempted", "true");
        window.location.reload(true);
      }
    }
  },
  true,
);

// Clear reload flag on successful load
window.addEventListener("load", () => {
  sessionStorage.removeItem("reload-attempted");
});
